<template>
  <div class="careers-page">
    <!-- ===== Hero Banner ===== -->
    <section class="hero-banner">
      <div class="hero-container">
        <div class="hero-content reveal">
          <h1>{{ $t('recruitmentTitle') }}</h1>
          <p class="hero-subtitle">{{ $t('recruitmentIntro') }}</p>
        </div>
      </div>
    </section>

    <!-- ===== Main Content ===== -->
    <main class="main-content">
      <div class="container">
        <!-- ===== Filters Section ===== -->
        <section class="filters-section reveal">
          <div class="section-header">
            <h4 class="section-tag">{{ $t('joinUs.filterTitle') }}</h4>
            <h2 class="section-title">{{ $t('joinUs.openPositions') }}</h2>
            <p class="results-count">{{ filteredJobs.length }} {{ $t('joinUs.positionsAvailable') }}</p>
          </div>

          <div class="filter-row">
            <div class="filter-group">
              <label for="department">{{ $t('joinUs.department') }}</label>
              <select id="department" v-model="tempFilters.department" class="filter-select">
                <option value="">{{ $t('joinUs.allDepartments') }}</option>
                <option v-for="dept in $t('joinUs.departments')" :key="dept" :value="dept">{{ dept }}</option>
              </select>
            </div>

            <div class="filter-group">
              <label for="location">{{ $t('joinUs.location') }}</label>
              <select id="location" v-model="tempFilters.location" class="filter-select">
                <option value="">{{ $t('joinUs.allLocations') }}</option>
                <option :value="$t('joinUs.beijing')">{{ $t('joinUs.beijing') }}</option>
                <option :value="$t('joinUs.shanghai')">{{ $t('joinUs.shanghai') }}</option>
                <option :value="$t('joinUs.guangzhou')">{{ $t('joinUs.guangzhou') }}</option>
                <option :value="$t('joinUs.zhengzhou')">{{ $t('joinUs.zhengzhou') }}</option>
                <option :value="$t('joinUs.chengdu')">{{ $t('joinUs.chengdu') }}</option>
                <option :value="$t('joinUs.chongqing')">{{ $t('joinUs.chongqing') }}</option>
                <option :value="$t('joinUs.ezhou')">{{ $t('joinUs.ezhou') }}</option>
              </select>
            </div>

            <div class="filter-group">
              <label for="jobType">{{ $t('joinUs.jobType') }}</label>
              <select id="jobType" v-model="tempFilters.jobType" class="filter-select">
                <option value="">{{ $t('joinUs.allJobTypes') }}</option>
                <option :value="$t('joinUs.fullTime')">{{ $t('joinUs.fullTime') }}</option>
              </select>
            </div>

            <div class="filter-actions">
              <button class="btn btn-primary" @click="applyFilters">
                <i class="el-icon-search"></i> {{ $t('joinUs.search') }}
              </button>
              <button class="btn btn-outline" @click="resetFilters">
                <i class="el-icon-refresh"></i> {{ $t('joinUs.resetFilters') }}
              </button>
            </div>
          </div>
        </section>

        <!-- ===== Job Listings ===== -->
        <section class="jobs-section reveal">
          <div class="job-grid">
            <article
              class="job-card"
              v-for="(job, index) in filteredJobs"
              :key="job.id"
              @click="selectedJob = job"
              :style="{ transitionDelay: (index * 0.05) + 's' }"
            >
              <div class="job-card-content">
                <div class="job-card-header">
                  <h3 class="job-title">{{ job.title }}</h3>
                  <span class="job-type-badge" :class="getJobTypeClass(job.type)">
                    {{ job.type }}
                  </span>
                </div>
                <div class="job-meta">
                  <span class="job-department">
                    <i class="el-icon-s-management"></i> {{ job.department }}
                  </span>
                  <span class="job-location">
                    <i class="el-icon-location-outline"></i> {{ job.location }}
                  </span>
                </div>
              </div>
              <div class="job-card-arrow">
                <router-link :to="'#'" class="arrow-link" @click.stop="selectedJob = job">
                  <i class="el-icon-top-right"></i>
                </router-link>
              </div>
            </article>

            <div v-if="filteredJobs.length === 0" class="no-results">
              <div class="no-results-icon">
                <i class="el-icon-document"></i>
              </div>
              <p>{{ $t('joinUs.noJobsFound') }}</p>
              <button @click="resetFilters" class="btn btn-primary">
                {{ $t('joinUs.resetFilters') }}
              </button>
            </div>
          </div>
        </section>

        <!-- ===== Benefits Section ===== -->
        <section class="benefits-section reveal">
          <div class="section-header text-center">
            <h4 class="section-tag">{{ $t('joinUs.benefits') }}</h4>
            <h2 class="section-title">{{ $t('joinUs.benefitsTitle') }}</h2>
          </div>

          <div class="benefits-grid">
            <div class="benefit-card" v-for="(benefit, index) in $t('joinUs.benefitList')" :key="index">
              <div class="benefit-icon-wrap">
                <i :class="getBenefitIconClass(index)"></i>
              </div>
              <h3>{{ $t(`joinUs.benefitTitles[${index}]`) }}</h3>
              <p>{{ benefit }}</p>
            </div>
          </div>
        </section>

        <!-- ===== CTA Section ===== -->
        <section class="cta-section reveal">
          <div class="cta-card">
            <div class="cta-content">
              <h3>{{ $t('joinUs.ctaHeading') }}</h3>
              <p>{{ $t('joinUs.ctaDescription') }}</p>
              <a href="mailto:haosijin@fst.net.cn" class="cta-email-link">
                <i class="el-icon-message"></i> haosijin@fst.net.cn
              </a>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- ===== Job Detail Modal ===== -->
    <transition name="modal">
      <div class="job-modal" v-if="selectedJob" @click.self="selectedJob = null">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="modal-header-left">
              <h2>{{ selectedJob.title }}</h2>
              <span class="modal-job-type" :class="getJobTypeClass(selectedJob.type)">
                {{ selectedJob.type }}
              </span>
            </div>
            <button class="modal-close" @click="selectedJob = null">
              <i class="el-icon-close"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="job-details-bar">
              <div class="detail-item">
                <i class="el-icon-s-management"></i>
                <span>{{ $t('joinUs.departmentLabel') }} {{ selectedJob.department }}</span>
              </div>
              <div class="detail-item">
                <i class="el-icon-location-outline"></i>
                <span>{{ $t('joinUs.locationLabel') }} {{ selectedJob.location }}</span>
              </div>
              <div class="detail-item">
                <i class="el-icon-time"></i>
                <span>{{ $t('joinUs.typeLabel') }} {{ selectedJob.type }}</span>
              </div>
            </div>

            <div class="job-description" v-html="selectedJob.description"></div>

            <div class="apply-block">
              <h4>{{ $t('joinUs.howToApply') }}</h4>
              <p>{{ $t('joinUs.contactPrompt') }}</p>
              <a href="mailto:haosijin@fst.net.cn" class="apply-email-link">
                <i class="el-icon-message"></i> haosijin@fst.net.cn
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Careers',
  data() {
    return {
      tempFilters: {
        department: '',
        location: '',
        jobType: ''
      },
      filters: {
        department: '',
        location: '',
        jobType: ''
      },
      selectedJob: null,
      observerInstances: [],
    };
  },
  computed: {
    filteredJobs() {
      const jobs = this.$t('joinUs.jobs');
      if (!Array.isArray(jobs)) return [];
      return jobs.filter(job => {
        return (
          (!this.filters.department || job.department === this.filters.department) &&
          (!this.filters.location || job.location === this.filters.location) &&
          (!this.filters.jobType || job.type === this.filters.jobType)
        );
      });
    }
  },
  methods: {
    getJobTypeClass(type) {
      const map = this.$t('joinUs.typeClassMap');
      return { [map[type] || '']: true };
    },
    getBenefitIconClass(index) {
      const icons = [
        'el-icon-money',
        'el-icon-reading',
        'el-icon-sunny',
        'el-icon-user-solid'
      ];
      return icons[index % 4];
    },
    applyFilters() {
      this.filters = {
        department: this.tempFilters.department,
        location: this.tempFilters.location,
        jobType: this.tempFilters.jobType
      };
    },
    resetFilters() {
      this.tempFilters = {
        department: '',
        location: '',
        jobType: ''
      };
      this.filters = {
        department: '',
        location: '',
        jobType: ''
      };
    },
    initRevealObserver() {
      if (!this.$el || !this.$el.querySelectorAll) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      );
      this.$el.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
      this.observerInstances.push(observer);
    },
  },
  watch: {
    selectedJob(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initRevealObserver();
    });
  },
  beforeDestroy() {
    this.observerInstances.forEach((obs) => obs.disconnect());
    document.body.style.overflow = '';
  },
};
</script>

<style lang="scss" scoped>
// ===== Design System Variables (aligned with Home.vue) =====
$primary: #0052CC;
$primary-dark: #0043A8;
$primary-light: #3B7FFF;
$silver: #8E9EAB;
$silver-light: #A8B5C0;
$dark: #0A1628;
$light: #F4F7FB;
$body: #5B6C7F;
$heading: #0A1628;

// ===== Global Page Wrapper =====
.careers-page {
  display: flex;
  flex-direction: column;
//   overflow-x: hidden;
  font-family: 'Rubik', 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: $body;
  line-height: 1.6;
  background: $light;
}

// ===== Hero Banner (aligned with About.vue) =====
.hero-banner {
  width: 100%;
  height: 60vh;
  min-height: 400px;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(10, 22, 40, .75) 0%, rgba(0, 82, 204, .3) 100%),
    url('../assets/images/zjwm.png') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 60, 120, 0.1);

  .hero-container {
    max-width: 1200px;
    width: 90%;
    margin: 0 auto;
    padding: 40px;
    background-color: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 32px rgba(0, 60, 120, 0.1);
  }

  .hero-content {
    max-width: 800px;
    text-align: center;
    animation: fadeInUp 1.2s ease-out;

    h1 {
      font-size: 2.6rem;
      font-weight: 800;
      margin-bottom: 1.5rem;
      color: #fff;
      text-shadow: 0 2px 12px rgba(0, 60, 120, 0.6);
      letter-spacing: -0.5px;
      line-height: 1.2;
      position: relative;
      display: inline-block;

      &::after {
        content: '';
        display: block;
        width: 80px;
        height: 4px;
        background: rgba(255, 255, 255, 0.7);
        margin: 20px auto;
        border-radius: 2px;
      }
    }

    .hero-subtitle {
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.8;
      max-width: 650px;
      margin: 0 auto;
    }
  }

  @media (max-width: 1200px) {
    height: 60vh;
    min-height: 500px;
  }

  @media (max-width: 1100px) {
    height: 55vh;
    min-height: 450px;
  }

  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;

    .hero-container {
      padding: 0 20px;
    }

    .hero-content {
      h1 { font-size: 2rem; }
      .hero-subtitle { font-size: 1rem; }
    }
  }

  @media (max-width: 480px) {
    height: 45vh;
    min-height: 350px;

    .hero-content h1 { font-size: 1.8rem; }
  }
}

// ===== Main Content Container =====
.main-content {
  flex: 1;
  width: 100%;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

// ===== Section Header (shared) =====
.section-header {
  margin-bottom: 40px;

  &.text-center {
    text-align: center;
  }

  .section-tag {
    font-size: 0.95rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    font-weight: 700;
    color: $primary;
    margin-bottom: 0.5rem;
  }

  .section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $heading;
    margin-bottom: 0.5rem;
    line-height: 1.2;
  }

  .results-count {
    font-size: 0.95rem;
    color: $silver;
    margin-top: 4px;
  }
}

// ===== Buttons (shared) =====
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.92rem;
  cursor: pointer;
  text-decoration: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  white-space: nowrap;

  i {
    font-size: 15px;
  }

  &.btn-primary {
    background: $primary;
    color: #fff;

    &:hover {
      background: $primary-dark;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 82, 204, 0.3);
    }
  }

  &.btn-outline {
    background: #fff;
    color: $primary;
    border: 1.5px solid rgba(0, 82, 204, 0.3);

    &:hover {
      background: rgba(0, 82, 204, 0.04);
      border-color: $primary;
      transform: translateY(-2px);
    }
  }
}

// ===== Filters Section =====
.filters-section {
  padding: 40px 0;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr) auto;
  gap: 20px;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;

  label {
    font-weight: 600;
    font-size: 0.88rem;
    color: $body;
    margin-bottom: 8px;
  }
}

.filter-select {
  padding: 11px 16px;
  border: 1px solid rgba(0, 82, 204, 0.15);
  border-radius: 8px;
  background: #fff;
  font-size: 0.95rem;
  color: $heading;
  transition: all 0.3s ease;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235B6C7F' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.08);
  }

  &:hover {
    border-color: $primary-light;
  }
}

.filter-actions {
  display: flex;
  gap: 12px;
  align-items: end;
}

// ===== Job Grid =====
.jobs-section {
  padding: 20px 0 60px;
}

.job-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.job-card {
  background: #fff;
  border-radius: 12px;
  padding: 28px 30px;
  border: 1px solid rgba(0, 82, 204, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.35s ease;
  opacity: 0;
  transform: translateY(20px);
  animation: cardFadeIn 0.5s ease forwards;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 82, 204, 0.12);
    border-color: rgba(0, 82, 204, 0.2);

    .job-card-arrow {
      background: $primary;
      color: #fff;
      transform: translateX(3px);
    }
  }
}

@keyframes cardFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.job-card-content {
  flex: 1;
  min-width: 0;
}

.job-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.job-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: $heading;
  margin: 0;
  line-height: 1.3;
}

.job-type-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;

  &.job-full-time {
    background: rgba(0, 82, 204, 0.08);
    color: $primary;
  }

  &.job-part-time {
    background: rgba(255, 152, 0, 0.08);
    color: #FF9800;
  }

  &.job-internship {
    background: rgba(46, 204, 113, 0.08);
    color: #2ecc71;
  }
}

.job-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  font-size: 0.88rem;
  color: $silver;

  span {
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      color: $primary;
      font-size: 15px;
    }
  }
}

.job-card-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 82, 204, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;

  .arrow-link {
    color: $primary;
    font-size: 16px;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
}

// ===== No Results =====
.no-results {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;

  .no-results-icon {
    font-size: 3rem;
    color: $silver-light;
    margin-bottom: 16px;
  }

  p {
    font-size: 1.05rem;
    color: $body;
    margin-bottom: 20px;
  }
}

// ===== Benefits Section =====
.benefits-section {
  padding: 40px 0 60px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.benefit-card {
  background: #fff;
  border-radius: 14px;
  padding: 32px 24px;
  text-align: center;
  border: 1px solid rgba(0, 82, 204, 0.06);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.35s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(0, 82, 204, 0.1);
    border-color: rgba(0, 82, 204, 0.15);

    .benefit-icon-wrap {
      background: $primary;
      color: #fff;
      transform: scale(1.1);
    }
  }

  .benefit-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(0, 82, 204, 0.08);
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;
    font-size: 1.5rem;
    transition: all 0.35s ease;
  }

  h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: $heading;
    margin-bottom: 10px;
  }

  p {
    font-size: 0.88rem;
    color: $body;
    line-height: 1.6;
    margin: 0;
  }
}

// ===== CTA Section =====
.cta-section {
  padding: 20px 0 60px;
}

.cta-card {
  background: linear-gradient(135deg, $dark 0%, $primary-dark 100%);
  border-radius: 16px;
  padding: 48px 40px;
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
  }

  .cta-content {
    position: relative;
    z-index: 1;

    h3 {
      font-size: 1.6rem;
      font-weight: 700;
      margin-bottom: 12px;
    }

    p {
      font-size: 0.95rem;
      opacity: 0.85;
      margin-bottom: 20px;
    }
  }

  .cta-email-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #fff;
    font-size: 1.15rem;
    font-weight: 600;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.15);
    padding: 12px 28px;
    border-radius: 30px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      transform: translateY(-2px);
    }
  }
}

// ===== Job Detail Modal =====
.job-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(10, 22, 40, 0.6);
  backdrop-filter: blur(4px);
}

.modal-container {
  background: #fff;
  border-radius: 16px;
  width: 90%;
  max-width: 760px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalSlideUp 0.35s ease;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 28px 32px 20px;
  border-bottom: 1px solid rgba(0, 82, 204, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .modal-header-left {
    flex: 1;

    h2 {
      font-size: 1.4rem;
      font-weight: 700;
      color: $heading;
      margin: 0 0 10px;
      line-height: 1.3;
    }

    .modal-job-type {
      display: inline-block;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 0.78rem;
      font-weight: 600;

      &.job-full-time {
        background: rgba(0, 82, 204, 0.08);
        color: $primary;
      }

      &.job-part-time {
        background: rgba(255, 152, 0, 0.08);
        color: #FF9800;
      }

      &.job-internship {
        background: rgba(46, 204, 113, 0.08);
        color: #2ecc71;
      }
    }
  }
}

.modal-close {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 82, 204, 0.06);
  color: $silver;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 82, 204, 0.12);
    color: $primary;
  }
}

.modal-body {
  padding: 24px 32px 32px;
}

.job-details-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding-bottom: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(0, 82, 204, 0.06);

  .detail-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    color: $body;

    i {
      color: $primary;
      font-size: 16px;
    }
  }
}

.job-description {
  margin-bottom: 28px;
  line-height: 1.7;
  color: $body;
  font-size: 0.92rem;

  ::v-deep h3 {
    font-size: 1.1rem;
    color: $heading;
    font-weight: 700;
    margin: 22px 0 10px;
  }

  ::v-deep ul {
    padding-left: 20px;
    margin-bottom: 16px;

    li {
      margin-bottom: 8px;
    }
  }
}

.apply-block {
  background: $light;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(0, 82, 204, 0.06);

  h4 {
    font-size: 1.05rem;
    font-weight: 700;
    color: $heading;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: $body;
    margin-bottom: 12px;
  }

  .apply-email-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: $primary;
    font-weight: 600;
    font-size: 1rem;
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
      color: $primary-dark;
      text-decoration: underline;
    }
  }
}

// ===== Modal Transition =====
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-to .modal-container {
  transform: translateY(20px);
  opacity: 0;
}

// ===== Reveal Animations =====
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.reveal.revealed {
  opacity: 1;
  transform: translateY(0);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ===== Responsive =====
@media (max-width: 1100px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    .filter-actions {
      grid-column: 1 / -1;
    }
  }
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .job-grid {
    grid-template-columns: 1fr;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .filter-row {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }

  .modal-container {
    width: 95%;
    max-height: 90vh;
  }

  .cta-card {
    padding: 36px 24px;
  }
}

@media (max-width: 480px) {
  .section-header .section-title {
    font-size: 1.4rem;
  }
}
</style>
